import React, { useState, useRef, useEffect } from 'react';
import SideNavigation from './SideNavigation';
import { useAuth } from '../lib/AuthContext';
import './DashboardLayout.css';
import { Link } from 'react-router';

interface DashboardLayoutProps {
    children: React.ReactNode;
    title?: string;
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children, title }) => {
    const [sidenavOpen, setSidenavOpen] = useState(false);
    const [showNotifications, setShowNotifications] = useState(false);
    const notificationRef = useRef<HTMLDivElement>(null);
    const { isAuthenticated, isLoading } = useAuth();

    const notifications: any[] = []; // Empty array to simulate no notifications

    // Close notification dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
                setShowNotifications(false);
            }
        };


        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);


    // Show loading while checking authentication
    if (isLoading) {
        return (
            <div className="dashboard-loading">
                <div className="dashboard-loading-spinner">
                    <i className="ri-loader-4-line"></i>
                </div>
                <div className="dashboard-loading-text">Loading...</div>
            </div>
        );
    }

    // If not authenticated, the ProtectedRoute wrapper will handle redirect
    if (!isAuthenticated) {
        return null;
    }

    const toggleSidenav = () => {
        setSidenavOpen(!sidenavOpen);
    };

    return (
        <div className="dashboard-layout">
            <SideNavigation isOpen={sidenavOpen} onToggle={toggleSidenav} />

            <div className="dashboard-main">
                {/* Mobile Header */}
                <header className="dashboard-header">
                    <button
                        className="dashboard-menu-btn"
                        onClick={toggleSidenav}
                        aria-label="Toggle navigation"
                    >
                        <i className="ri-menu-3-line"></i>
                    </button>

                    {title && (
                        <h1 className="dashboard-header-title">{title}</h1>
                    )}

                    <div className="dashboard-header-actions">
                        {/* Notification Bell */}
                        <div className="notification-container" ref={notificationRef}>
                            <button
                                className="dashboard-action-btn"
                                onClick={() => setShowNotifications(!showNotifications)}
                                aria-label="Notifications"
                            >
                                <i className="ri-notification-3-line"></i>
                                {notifications.length > 0 ? (
                                    <span className="dashboard-notification-badge">{notifications.length}</span>
                                ) : null}
                            </button>

                            {showNotifications && (
                                <div className="notification-dropdown">
                                    <div className="notification-header">
                                        <h3>Notifications</h3>
                                    </div>
                                    <div className="notification-content">
                                        {notifications.length === 0 ? (
                                            <div className="no-notifications">
                                                <i className="ri-notification-off-line"></i>
                                                <p>You don't have any notifications yet</p>
                                            </div>
                                        ) : (
                                            notifications.map((notification, index) => (
                                                <div key={index} className="notification-item">
                                                    {notification.message}
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Settings */}
                        <Link to="/dashboard/profile" className="dashboard-action-btn"
                            aria-label="Settings"
                            style={{ textDecoration: 'none' }}
                            >
                            <i className="ri-settings-3-line"></i>
                        </Link>
                    </div>
                </header>

                {/* Main Content */}
                <main className="dashboard-content">
                    {children}
                </main>
            </div>
        </div>
    );
};

export default DashboardLayout;
